import { FunctionComponent, useCallback, useContext, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useNavigate, useParams } from 'react-router-dom';
import { FeedCard } from '../../../../components';
import { ConfigContext, UserContext } from '../../../../context';
import { StoryCardStatus, StoryCardType } from '../../../../services';
import {
  FeedCardDrag,
  FeedCardLoader,
  IconLabel,
  SortableList,
  useAppDispatch,
  useAppSelector,
  useFeedCardReorder,
  useHasHistory,
} from '../../../../shared';
import {
  getStoryCardsUserProfileFeed,
  loadMoreStoryCardsUserProfileFeed,
  updateProfileFeedCardPosition,
} from '../../../../slices';
import { NoContent } from '../../../Error';
import { ProfileFeedCreateCard, ProfileFeedCreateCardMode } from '../ProfileFeedCreateCard';

import classes from './ProfileFeedItems.module.scss';

export const ProfileFeedItems: FunctionComponent = () => {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const dispatch = useAppDispatch();

  const { enabled } = useContext(ConfigContext).config.elements.profile.feed;

  const { userProfile } = useContext(UserContext);

  const { profileId } = useParams();

  const isProfileOther = useMemo(() => Boolean(profileId), [profileId]);

  const userId = Number(profileId ?? userProfile.id);

  const hasHistory = useHasHistory();

  const {
    storyCardsUserProfileFeed: cards,
    page,
    hasNextPage,
    isFetched,
  } = useAppSelector(({ storyCardsUserProfileFeed }) => storyCardsUserProfileFeed);

  const reorderHandler = useFeedCardReorder({
    cards,
    updatePosition: updateProfileFeedCardPosition,
  });

  useEffect(() => {
    if (!enabled) {
      return;
    }

    dispatch(
      getStoryCardsUserProfileFeed({
        userId,
        filters: {
          storyCardType: [StoryCardType.IMAGE, StoryCardType.VIDEO],
          ...(isProfileOther && { status: [StoryCardStatus.PUBLISHED] }),
        },
      })
    );
  }, [dispatch, enabled, isProfileOther, userId]);

  const loadMoreStoryCardsUserProfileFeedItems = useCallback(() => {
    dispatch(
      loadMoreStoryCardsUserProfileFeed({
        userId,
        page: page + 1,
        filters: {
          storyCardType: [StoryCardType.IMAGE, StoryCardType.VIDEO],
          ...(isProfileOther && { status: [StoryCardStatus.PUBLISHED] }),
        },
      })
    );
  }, [dispatch, isProfileOther, page, userId]);

  const content = useMemo(() => {
    if (!Boolean(cards.length)) {
      return <NoContent subtitle={t('profileFeed.no-content')} />;
    }

    const sortableCardIds = cards
      .filter(({ permissions }) => permissions.allowToUpdatePosition)
      .map(({ id }) => id);

    return (
      <InfiniteScroll
        next={loadMoreStoryCardsUserProfileFeedItems}
        hasMore={hasNextPage}
        loader={<FeedCardLoader />}
        dataLength={cards.length}
        className={classes['profile-feed__content']}
      >
        <div className={classes['profile-feed__content-cards']}>
          <SortableList items={sortableCardIds} onReorder={reorderHandler}>
            {cards.map((card) => {
              const { id: storyCardId } = card;

              if (!sortableCardIds.includes(storyCardId)) {
                return <FeedCard key={storyCardId} card={card} isProfileFeedCard />;
              }

              return (
                <FeedCardDrag key={storyCardId} storyCardId={storyCardId}>
                  <FeedCard card={card} isProfileFeedCard />
                </FeedCardDrag>
              );
            })}
          </SortableList>
        </div>
      </InfiniteScroll>
    );
  }, [cards, hasNextPage, loadMoreStoryCardsUserProfileFeedItems, reorderHandler, t]);

  if (!enabled) {
    return null;
  }

  if (!isFetched) {
    return <FeedCardLoader />;
  }

  return (
    <div className={classes['profile-feed']}>
      <h1 className={classes['profile-feed__title']}>{t('profileFeed.title')}</h1>
      <div className={classes['profile-feed__header']}>
        <IconLabel
          {...(hasHistory && { iconId: 'arrow-rounded-left' })}
          label={t('profileFeed.title')}
          labelClassName={classes['profile-feed__header-title']}
          onClick={() => hasHistory && navigate(-1)}
          color={'#43445b'}
          singleColor
        />
        {!isProfileOther && <ProfileFeedCreateCard mode={ProfileFeedCreateCardMode.BUTTON} />}
      </div>
      {content}
    </div>
  );
};
